import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from 'react-oidc-context';
import DiagnosisService from '../../services/diagnosisService';
import './DiagnosisDropdown.css';

const DiagnosisDropdown = ({ 
  selectedDiagnosis, 
  onDiagnosisSelect, 
  placeholder = "Buscar diagnóstico...",
  disabled = false 
}) => {
  const auth = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [diagnoses, setDiagnoses] = useState([]);
  const [filteredDiagnoses, setFilteredDiagnoses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const loadDiagnoses = useCallback(async () => {
    if (!auth.user?.access_token) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await DiagnosisService.getAllDiagnoses(auth.user.access_token);
      const diagnosesData = response.data || [];
      setDiagnoses(diagnosesData);
    } catch (err) {
      console.error('Error loading diagnoses:', err);
      setError('Error al cargar diagnósticos');
    } finally {
      setLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load initial diagnoses
  useEffect(() => {
    loadDiagnoses();
  }, [loadDiagnoses]);

  // Filter diagnoses based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDiagnoses(diagnoses.slice(0, 10)); // Show first 10 when no search
    } else {
      const filtered = diagnoses.filter(diagnosis =>
        diagnosis.name.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 10); // Limit results to 10
      setFilteredDiagnoses(filtered);
    }
  }, [searchTerm, diagnoses]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    // Use a longer delay to ensure the toggle event completes first
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen]);

  // Position dropdown function
  const positionDropdown = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current;

    menu.style.top = `${triggerRect.bottom + 4}px`;
    menu.style.left = `${triggerRect.left}px`;
    menu.style.width = `${triggerRect.width}px`;
    menu.style.maxWidth = `${triggerRect.width}px`;
  }, []);

  // Focus search input when dropdown opens and position dropdown
  useEffect(() => {
    if (!isOpen) return;

    let rafId = requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      positionDropdown();
    });

    // Reposition on scroll/resize
    const handleReposition = () => {
      rafId = requestAnimationFrame(positionDropdown);
    };

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, positionDropdown]);

  const handleToggleDropdown = (e) => {
    if (disabled) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    setIsOpen(prev => {
      const newState = !prev;
      if (!newState) {
        // Si se está cerrando, limpiar el término de búsqueda
        setSearchTerm('');
      }
      return newState;
    });
  };

  const handleDiagnosisSelect = (diagnosis) => {
    onDiagnosisSelect(diagnosis);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSelection = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDiagnosisSelect(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (e.key === 'Enter' && filteredDiagnoses.length > 0) {
      handleDiagnosisSelect(filteredDiagnoses[0]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // TODO: Add keyboard navigation if needed
    }
  };

  const displayText = selectedDiagnosis 
    ? selectedDiagnosis.name 
    : placeholder;

  return (
    <div className={`diagnosis-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div 
        ref={triggerRef}
        className={`dropdown-trigger ${isOpen ? 'open' : ''} ${selectedDiagnosis ? 'has-selection' : ''}`}
        onClick={handleToggleDropdown}
      >
        <div className="selected-value">
          <i className="fas fa-stethoscope"></i>
          <span className={selectedDiagnosis ? 'selected' : 'placeholder'}>
            {displayText}
          </span>
        </div>
        <div className="dropdown-actions">
          {selectedDiagnosis && (
            <button
              type="button"
              className="clear-btn"
              onClick={handleClearSelection}
              title="Limpiar selección"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
          <i className={`fas fa-chevron-down dropdown-arrow ${isOpen ? 'open' : ''}`}></i>
        </div>
      </div>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="dropdown-menu diagnosis-dropdown-portal"
          style={{
            position: 'fixed',
            display: 'flex',
            visibility: 'visible',
            opacity: 1
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="search-container">
            <i className="fas fa-search search-icon"></i>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Buscar diagnóstico..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="diagnoses-list">
            {loading ? (
              <div className="loading-item">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Cargando diagnósticos...</span>
              </div>
            ) : error ? (
              <div className="error-item">
                <i className="fas fa-exclamation-triangle"></i>
                <span>{error}</span>
              </div>
            ) : filteredDiagnoses.length === 0 ? (
              <div className="empty-item">
                <i className="fas fa-search"></i>
                <span>
                  {diagnoses.length === 0
                    ? 'No hay diagnósticos disponibles. Crea algunos en la sección de Diagnósticos.'
                    : searchTerm
                      ? 'No se encontraron diagnósticos'
                      : 'No hay diagnósticos disponibles'}
                </span>
              </div>
            ) : (
              filteredDiagnoses.map((diagnosis) => (
                <div
                  key={diagnosis.id}
                  className={`diagnosis-item ${selectedDiagnosis?.id === diagnosis.id ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDiagnosisSelect(diagnosis);
                  }}
                >
                  <div className="diagnosis-info">
                    <div className="diagnosis-name">{diagnosis.name}</div>
                    <div className="diagnosis-details">
                      <span
                        className="diagnosis-color-dot"
                        style={{ backgroundColor: diagnosis.color }}
                        title={`Color: ${diagnosis.color}`}
                      ></span>
                      <span className="diagnosis-type">
                        {diagnosis.isCompleteTooth ? 'Diente completo' : 'Por cara'}
                      </span>
                    </div>
                  </div>
                  {selectedDiagnosis?.id === diagnosis.id && (
                    <i className="fas fa-check selected-icon"></i>
                  )}
                </div>
              ))
            )}
          </div>

          {searchTerm && filteredDiagnoses.length > 0 && (
            <div className="search-footer">
              <span className="results-count">
                {filteredDiagnoses.length} resultado{filteredDiagnoses.length !== 1 ? 's' : ''} encontrado{filteredDiagnoses.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DiagnosisDropdown;
